import { useQuery } from "react-query";
import { clearStoredAuth } from "../utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useDeleteUser,
  useResetPassword,
  useResetPin,
  useReactivateUserLogin,
} from "../hooks/useUser";
import { API_URL } from "../config";
import { EMPTY_USER } from "../constants";

const fetchUsers = async () => {
  const res = await fetch(`${API_URL}/users`, {
    credentials: "include",
  });
  // TODO: process error
  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const useUsers = () => {
  const [selectedUser, setSelectedUser] = useState(EMPTY_USER);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isResetPinModalOpen, setIsResetPinModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  
  const navigate = useNavigate();

  const deleteMutation = useDeleteUser();
  const resetPasswordMutation = useResetPassword();
  const resetPinMutation = useResetPin();
  const reactivateLoginMutation = useReactivateUserLogin();

  const handleOpenDeleteModal = (user: User) => {
    if (user.id) {
      setSelectedUser(user);
      setIsDeleteModalOpen(true);
    }
  }

  const handleDeleteUser = () => {
    if (selectedUser.id) {
      deleteMutation.mutate(selectedUser.id);
      setIsDeleteModalOpen(false);
    }
  }

  const handleOpenEditModal = (user: User) => {
    if (user.id) {
      setSelectedUser(user);
      setIsEditModalOpen(true);
    }
  }

  const handleOpenResetPasswordModal = (user: User) => {
    if (user.id) {
      setSelectedUser(user);
      setIsResetPasswordModalOpen(true);
    }
  }

  const handleOpenResetPinModal = (user: User) => {
    if (user.id) {
      setSelectedUser(user);
      setIsResetPinModalOpen(true);
    }
  }

  const handleResetPassword = () => {
    if (selectedUser.id) {
      resetPasswordMutation.mutate(selectedUser);
      setIsResetPasswordModalOpen(false);
    }
  }

  const handleResetPin = () => {
    if (selectedUser.id) {
      resetPinMutation.mutate(selectedUser);
      setIsResetPinModalOpen(false);
    }
  }

  const handleOpenReactivateModal = (user: User) => {
    if (user.id) {
      setSelectedUser(user);
      reactivateLoginMutation.reset();
      setIsReactivateModalOpen(true);
    }
  };

  const handleReactivateLogin = () => {
    if (selectedUser.id) {
      reactivateLoginMutation.mutate(selectedUser, {
        onSuccess: () => setIsReactivateModalOpen(false),
      });
    }
  };

  const users = useQuery(["users"], () => fetchUsers(), {
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  })

  return {
    users,
    setIsDeleteModalOpen,
    handleOpenDeleteModal,
    isDeleteModalOpen,
    handleDeleteUser,
    setIsEditModalOpen,
    handleOpenEditModal,
    isEditModalOpen,
    setIsResetPasswordModalOpen,
    handleOpenResetPasswordModal,
    isResetPasswordModalOpen,
    handleResetPassword,
    setIsResetPinModalOpen,
    handleOpenResetPinModal,
    isResetPinModalOpen,
    handleResetPin,
    setIsReactivateModalOpen,
    handleOpenReactivateModal,
    isReactivateModalOpen,
    handleReactivateLogin,
    selectedUser,
    isReactivateLoading: reactivateLoginMutation.status === "loading",
    reactivateError: reactivateLoginMutation.isError
      ? "Failed to reactivate login. Please try again."
      : null,
  };
};

const fetchOperators = async () => {
  const res = await fetch(`${API_URL}/operators`, {
    credentials: "include",
  });
  // TODO: process error
  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const useOperators = (enabled = true) => {
  const navigate = useNavigate();

  return useQuery(["operators"], () => fetchOperators(), {
    enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};
